import React, { useEffect, useState } from 'react';
import { FaHome, FaBuilding, FaUser, FaBriefcase } from 'react-icons/fa';
import { getEstates } from '../../../../services/broker';
import {
  StepContainer,
  SectionHeader,
  SectionTitle,
  SectionDivider,
  SectionDescription,
  FormGroup,
  Label,
  Select,
  CardGrid,
  SelectionCard,
  CardIcon,
  CardLabel,
  ButtonContainer,
  NextButton,
  ErrorMessage,
  LoadingSpinner,
} from '../../styles';
import { MobileAnalysisFormData, EstateOption, PropertyPurpose, TenantType } from '../../types';

interface Step1Props {
  formData: MobileAnalysisFormData;
  updateFormData: (data: Partial<MobileAnalysisFormData>) => void;
  onNext: () => void;
  userId?: string;
  userEstateId?: string;
  userEstateName?: string;
  brokerId?: string;
}

const Step1_TenantType: React.FC<Step1Props> = ({
  formData,
  updateFormData,
  onNext,
  userId,
  userEstateId,
  userEstateName,
  brokerId,
}) => {
  const [estates, setEstates] = useState<EstateOption[]>([]);
  const [loadingEstates, setLoadingEstates] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadEstates = async () => {
      try {
        // If user belongs to an estate, use that directly
        if (userEstateId) {
          setEstates([{ id: userEstateId, name: userEstateName || 'Minha Imobiliária' }]);
          updateFormData({ estateId: userEstateId, estateName: userEstateName || 'Minha Imobiliária' });
          setLoadingEstates(false);
          return;
        }

        // Otherwise, load estates from broker
        if (brokerId) {
          const estatesList = await getEstates(brokerId);
          const mappedEstates = estatesList.map((e: any) => ({
            id: e.id,
            name: e.name,
            cnpj: e.cnpj,
          }));
          setEstates(mappedEstates);

          // Auto-select if only one estate
          if (mappedEstates.length === 1) {
            updateFormData({
              estateId: mappedEstates[0].id,
              estateName: mappedEstates[0].name,
            });
          }
        }
      } catch (err) {
        console.error('Error loading estates:', err);
        setError('Erro ao carregar imobiliárias');
      } finally {
        setLoadingEstates(false);
      }
    };

    loadEstates();
  }, [brokerId, userEstateId, userEstateName]);

  const handleEstateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedEstate = estates.find((estate) => estate.id === e.target.value);
    updateFormData({
      estateId: e.target.value,
      estateName: selectedEstate?.name || '',
    });
  };

  const handlePurposeChange = (purpose: PropertyPurpose) => {
    updateFormData({ purpose });
  };

  const handleTenantTypeChange = (tenantType: TenantType) => {
    updateFormData({ tenantType });
  };

  const canProceed = formData.estateId && formData.purpose && formData.tenantType;

  const handleNext = () => {
    if (canProceed) {
      onNext();
    }
  };

  if (loadingEstates) {
    return (
      <StepContainer style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <LoadingSpinner />
      </StepContainer>
    );
  }

  return (
    <StepContainer>
      <SectionHeader>
        <SectionTitle>Tipo de Análise</SectionTitle>
        <SectionDivider />
      </SectionHeader>

      <SectionDescription>
        Selecione a imobiliária e o tipo de locação para iniciar a análise.
      </SectionDescription>

      {/* Estate Selection */}
      <FormGroup>
        <Label required>Imobiliária</Label>
        <Select
          value={formData.estateId}
          onChange={handleEstateChange}
          disabled={estates.length === 1}
        >
          {estates.length !== 1 && <option value="">Selecione uma imobiliária</option>}
          {estates.map((estate) => (
            <option key={estate.id} value={estate.id}>
              {estate.name}
            </option>
          ))}
        </Select>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </FormGroup>

      {/* Property Purpose */}
      <FormGroup>
        <Label required>Tipo do Imóvel</Label>
        <CardGrid>
          <SelectionCard
            type="button"
            selected={formData.purpose === 'RESIDENTIAL'}
            onClick={() => handlePurposeChange('RESIDENTIAL')}
          >
            <CardIcon selected={formData.purpose === 'RESIDENTIAL'}>
              <FaHome />
            </CardIcon>
            <CardLabel selected={formData.purpose === 'RESIDENTIAL'}>
              Residencial
            </CardLabel>
          </SelectionCard>

          <SelectionCard
            type="button"
            selected={formData.purpose === 'COMMERCIAL'}
            onClick={() => handlePurposeChange('COMMERCIAL')}
          >
            <CardIcon selected={formData.purpose === 'COMMERCIAL'}>
              <FaBuilding />
            </CardIcon>
            <CardLabel selected={formData.purpose === 'COMMERCIAL'}>
              Comercial
            </CardLabel>
          </SelectionCard>
        </CardGrid>
      </FormGroup>

      {/* Tenant Type */}
      <FormGroup>
        <Label required>Tipo do Locatário</Label>
        <CardGrid>
          <SelectionCard
            type="button"
            selected={formData.tenantType === 'NATURAL'}
            onClick={() => handleTenantTypeChange('NATURAL')}
          >
            <CardIcon selected={formData.tenantType === 'NATURAL'}>
              <FaUser />
            </CardIcon>
            <CardLabel selected={formData.tenantType === 'NATURAL'}>
              Pessoa Física
            </CardLabel>
          </SelectionCard>

          <SelectionCard
            type="button"
            selected={formData.tenantType === 'LEGAL'}
            onClick={() => handleTenantTypeChange('LEGAL')}
          >
            <CardIcon selected={formData.tenantType === 'LEGAL'}>
              <FaBriefcase />
            </CardIcon>
            <CardLabel selected={formData.tenantType === 'LEGAL'}>
              Pessoa Jurídica
            </CardLabel>
          </SelectionCard>
        </CardGrid>
      </FormGroup>

      <ButtonContainer>
        <NextButton
          type="button"
          disabled={!canProceed}
          onClick={handleNext}
        >
          Próximo
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default Step1_TenantType;
